/**
 * NotificationCard — a single notification row for the bell dropdown and list page.
 *
 * Layout: kind icon + title (bold if unread) + body (1-line truncate) + relative time.
 * The whole card is clickable → router.push(notification.link) AND triggers markRead.
 * Unread cards: left border-accent (2px primary) + bold title.
 * Read cards: muted appearance.
 *
 * Accessibility:
 * - role="article" with aria-labelledby pointing at the title.
 * - aria-current="true" when the card is unread (indicates relevance to the user).
 */

import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

import { cn } from '@/lib/utils';
import { parseBackendDate } from '@/lib/utils/formatters';
import type { NotificationListItem } from '@/lib/api/notifications';
import { NotificationKindBadge } from './NotificationKindBadge';
import { useMarkReadMutation } from '@/hooks/api/useNotifications';

// ── Relative time helper ──────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  try {
    return formatDistanceToNow(parseBackendDate(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export interface NotificationCardProps {
  notification: NotificationListItem;
  /** When true, clicking still marks as read but does not navigate. Used in the bell dropdown. */
  compact?: boolean;
}

export function NotificationCard({ notification, compact = false }: NotificationCardProps) {
  const navigate = useNavigate();
  const markReadMutation = useMarkReadMutation();
  const isUnread = notification.read_at == null;
  const titleId = `notif-title-${notification.id}`;

  function handleClick() {
    if (isUnread) {
      markReadMutation.mutate({ notification_ids: [notification.id] });
    }
    if (notification.link && !compact) {
      navigate(notification.link);
    } else if (notification.link) {
      navigate(notification.link);
    }
  }

  return (
    <article
      role="article"
      aria-labelledby={titleId}
      aria-current={isUnread ? 'true' : undefined}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      tabIndex={0}
      className={cn(
        'flex flex-col gap-1 px-3 py-2.5 cursor-pointer rounded-md transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        'hover:bg-muted/60',
        isUnread
          ? 'border-l-2 border-primary pl-[10px] bg-primary/5'
          : 'border-l-2 border-transparent text-muted-foreground'
      )}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <NotificationKindBadge kind={notification.kind} size="sm" />
        <time
          dateTime={notification.created_at}
          className="text-[10px] text-muted-foreground shrink-0 tabular-nums"
        >
          {relativeTime(notification.created_at)}
        </time>
      </div>

      <p
        id={titleId}
        className={cn(
          'text-sm leading-snug truncate',
          isUnread ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground'
        )}
      >
        {notification.title}
      </p>

      {notification.body && (
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-1">
          {notification.body}
        </p>
      )}
    </article>
  );
}
